import { inject, observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { LicenseJsonModel, ModeJsonModel } from "../models/BlockTransportSystemJsonModel";
import BlockKitStore from "../stores/BlockKitStore";
import LicenseInfoCard from "../components/LicenseInfoCard/LicenseInfoCard";
import { AllCommunityModule, ICellRendererParams, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import GlobalEnvironments from "../constants/GlobalEnvironments";
import { getLocaleString } from "../utils/DateHelper";
import { Badge, Button, Container, Spinner, Stack } from "react-bootstrap";
import PermissionModal from "../PermissionModal/PermissionModal";
import StudioUserStore from "../stores/StudioUserStore";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import LicenseModeEditModal from "../components/LicenseModeEditModal/LicenseModeEditModal";

export interface IProps {
  blockKitStore?: BlockKitStore;
  studioUserStore?: StudioUserStore;
}

ModuleRegistry.registerModules([AllCommunityModule]);

const LicensePage: React.FC<IProps> = inject("blockKitStore", "studioUserStore")(observer(({
  blockKitStore,
  studioUserStore
}) => {
  const [selectedLicense, setSelectedLicense] = useState<LicenseJsonModel>();
  const [isShowEditModal, setIsShowEditModal] = useState<boolean>(false);
  const [isShowPermissionModal, setIsShowPermissionModal] = useState<boolean>(false);
  const [isShowLicenseModeEditModal, setIsShowLicenseModeEditModal] = useState<boolean>(false);
  const [isCreateMode, setIsCreateMode] = useState<boolean>(false);
  const [userModes, setUserModes] = useState<ModeJsonModel[]>([]);
  const [isWorking, setIsWorking] = useState<boolean>(false);
  const { t } = useTranslation();

  const ImageCell = (props: ICellRendererParams) => {
    const imgSrc = `${GlobalEnvironments.getBlockTransportSystemHostUrlForWeb()}${props.value}`;
    return props.value ? (
      <a href={imgSrc} target="_blank" rel="noopener noreferrer">
        <img
          src={imgSrc}
          alt="thumbnail"
          style={{ width: "100%", height: "100%", objectFit: 'contain' }}
        />
      </a>
    ) : null;
  };

  const ModeButtonCell: React.FC<ICellRendererParams<LicenseJsonModel>> = (params) => {
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={async () => {
          setSelectedLicense(params.data);
          setIsShowLicenseModeEditModal(true);
          params.api.deselectAll();
          params.node.setSelected(true);
        }}>
        {`${t("button-mode")}(${params.data?.exposureModesCount})`}
      </Button>
    );
  }

  useEffect(() => {
    blockKitStore?.initLicensePageAsync();
    blockKitStore?.studioController.getUserModesAsync().then(setUserModes);
  }, []);

  return (
    <Container fluid style={{ height: "100dvh", overflowY: "auto" }}>
      {isShowEditModal &&
        <LicenseInfoCard
          isCreateMode={isCreateMode}
          isShow={isShowEditModal}
          license={selectedLicense}
          timeZone={blockKitStore?.timeZone}
          onClickSave={async (license: LicenseJsonModel, file?: File) => {
            if (confirm(t("edit-confirmation-message")) === false) {
              return;
            }

            if (!license?.id) {
              license.id = uuidv4();
            }
            const result = await blockKitStore?.updateLicenseAsync(license, file);
            if (result) {
              await blockKitStore?.studioController.postLicenseStagingAsync(result);
              await blockKitStore?.initLicensePageAsync();
            }

            setIsShowEditModal(false);
          }}
          onClickClose={() => setIsShowEditModal(false)}
        />}
      {isShowLicenseModeEditModal &&
        <LicenseModeEditModal
          isShow={isShowLicenseModeEditModal}
          license={selectedLicense}
          modes={userModes}
          onSave={async (modeIds: string[]) => {
            if (selectedLicense?.id) {
              await blockKitStore?.studioController?.postLicenseModesStagingAsync(selectedLicense?.id, modeIds)
              await blockKitStore?.initLicensePageAsync();
              setIsShowLicenseModeEditModal(false);
            } else {
              alert(t("license-id-invalid-message"));
            }
          }}
          onClose={() => setIsShowLicenseModeEditModal(false)}
        />}
      <PermissionModal<LicenseJsonModel>
        entityType="licenses"
        entity={selectedLicense}
        isShow={isShowPermissionModal}
        onClose={() => setIsShowPermissionModal(false)}
      />
      <Stack className="m-2" direction="horizontal" gap={2}>
        <Button
          onClick={() => {
            setIsCreateMode(true);
            setIsShowEditModal(true);
          }}>{t("button-new")}</Button>
        <Button
          disabled={!selectedLicense}
          onClick={() => {
            setIsCreateMode(false);
            setIsShowEditModal(true);
          }}>{t("button-edit")}</Button>
        <Button
          disabled={!selectedLicense || isWorking}
          onClick={async () => {
            setIsWorking(true);
            try {
              if (!confirm(t("publish-confirmation-message"))) {
                return;
              }

              const response = await blockKitStore?.studioController.publishLicenseStagingAsync(selectedLicense!);
              if (response?.isSuccess) {
                await blockKitStore?.initLicensePageAsync();
                alert(t("publish-success"));
              } else {
                alert(`${t("publish-failed")} (${response?.message})`)
              }
            } finally {
              setIsWorking(false);
            }
          }}>
          {isWorking &&
            <Spinner
              className="me-1"
              animation="border"
              size="sm"
              role="status"
            />}
          {t("button-publish")}
        </Button>
        <Button
          variant="danger"
          disabled={!selectedLicense || isWorking}
          onClick={async () => {
            if (confirm(t("delete-confirmation-message")) === false) {
              return;
            }

            if (!selectedLicense) {
              return;
            }

            var response = await blockKitStore?.studioController.deleteLicenseStagingAsync(selectedLicense);
            if (response.isSuccess) {
              await blockKitStore?.initLicensePageAsync();
            }

            alert(response.message);
          }}>
          {t("button-delete")}
        </Button>
        <Button
          hidden={!studioUserStore?.hasPermission("license:permission")}
          disabled={!selectedLicense}
          variant="warning"
          onClick={() => setIsShowPermissionModal(true)}>{t("button-permission")}</Button>
      </Stack>
      <div style={{ height: "60dvh" }}>
        <AgGridReact<LicenseJsonModel>
          getRowId={params => params.data.id!}
          rowData={blockKitStore?.allLicenses}
          columnDefs={[
            {
              headerName: t("publish"),
              width: 80,
              cellRenderer: (params: ICellRendererParams) => {
                return blockKitStore?.publishedLicenses.some(i => i.id === params.data?.id)
                  ? <Badge bg="info">{t("published")}</Badge>
                  : null
              },
            },
            {
              headerName: t("thumbnail"),
              field: "thumbnailUrl",
              width: 90,
              cellRenderer: ImageCell,
            },
            {
              headerName: t("id"),
              field: "id",
              filter: true
            },
            {
              headerName: t("name"),
              field: "name",
              filter: true,
            },
            {
              headerName: t("block-count"),
              filter: "agNumberColumnFilter",
              width: 120,
              valueGetter: parmas => blockKitStore?.allBlocks?.filter(block => block.data.licenseId === parmas.data?.id)?.length
            },
            {
              headerName: t("mode"),
              cellRenderer: ModeButtonCell,
              width: 90
            },
            {
              headerName: t("release-date"),
              field: "releaseDate",
              filter: "agDateColumnFilter",
              valueFormatter: parmas => parmas.data ? getLocaleString(new Date(parmas.data.releaseDate), blockKitStore?.timeZone) : "",
            },
            {
              headerName: t("expire-date"),
              field: "expireDate",
              filter: "agDateColumnFilter",
              valueFormatter: params => params.data ? getLocaleString(new Date(params.data.expireDate), blockKitStore?.timeZone) : ""
            }
          ]}
          rowSelection={{
            mode: "singleRow",
            enableClickSelection: false,
          }}
          onSelectionChanged={(e) => {
            const selected = e.api.getSelectedRows();
            if (selected.length > 0) {
              setSelectedLicense(selected[0]);
            } else {
              setSelectedLicense(undefined);
            }
          }}
        />
      </div>
    </Container>
  )
}));

export default LicensePage;